import { useQuery } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { SubscriptionBillingPeriod, SubscriptionTier } from '@/types/dtos/transactions/subscription-plan.dto';

export const SUBSCRIPTION_CHECKOUT_CART_QUERY_KEY = 'subscription-checkout-cart';

/** Normalized plan row from cart `lines[0]` (seeds manage-plan quota preview). */
export type SubscriptionCheckoutCartPlanChangeDraft = {
  tier: SubscriptionTier;
  billingPeriod?: SubscriptionBillingPeriod;
  additionalQuotaUnits?: { quotaKey: string; quantity: number }[];
};

export function parseCheckoutCartPlanChange(raw: unknown): SubscriptionCheckoutCartPlanChangeDraft | undefined {
  if (!raw || typeof raw !== 'object' || raw === null || !('tier' in raw)) {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  const tier = o.tier as SubscriptionTier;
  if (!tier) {
    return undefined;
  }
  return {
    tier,
    billingPeriod: o.billingPeriod as SubscriptionBillingPeriod | undefined,
    additionalQuotaUnits: Array.isArray(o.additionalQuotaUnits)
      ? (o.additionalQuotaUnits as { quotaKey: string; quantity: number }[])
      : undefined,
  };
}

const TIER_QUOTA_CARD_PERIOD_SUFFIX = /-(MONTH|YEAR)$/;

/**
 * Merges legacy manage-plan keys (`FREE-MONTH`, `BASIC-YEAR`) into billing-independent tier keys (`FREE`, `BASIC`).
 * When both exist, keeps the higher extra count per quota key.
 */
export function normalizeQuotaExtrasByTierKeys(
  map: Record<string, Record<string, number>>,
): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  for (const [key, extras] of Object.entries(map)) {
    const tierKey = TIER_QUOTA_CARD_PERIOD_SUFFIX.test(key) ? key.replace(TIER_QUOTA_CARD_PERIOD_SUFFIX, '') : key;
    const target = { ...(out[tierKey] ?? {}) };
    for (const [quotaKey, q] of Object.entries(extras)) {
      target[quotaKey] = Math.max(target[quotaKey] ?? 0, q);
    }
    out[tierKey] = target;
  }
  return out;
}

/** Maps draft `additionalQuotaUnits` to ManagePlanDialog quota preview (key = tier only; shared across billing periods). */
export function quotaExtrasByCardFromPlanChangeDraft(
  draft: SubscriptionCheckoutCartPlanChangeDraft,
): Record<string, Record<string, number>> {
  const cardKey = draft.tier;
  const extras: Record<string, number> = {};
  for (const u of draft.additionalQuotaUnits ?? []) {
    if (u.quantity > 0) {
      extras[u.quotaKey] = u.quantity;
    }
  }
  return { [cardKey]: extras };
}

export type SubscriptionCheckoutCartPlanLineTotal = {
  subscriptionProductKey: string;
  tier: string;
  billingPeriod?: string;
  amountEur: number;
  /** Gross new-plan portion for remainder (mid-cycle); net due is {@link amountEur}. */
  estimatedNewPlanPortionEur?: number;
  /** Credit for unused time on previous plan (mid-cycle), same currency as amounts. */
  estimatedCreditForUnusedTimeEur?: number;
  prorationWindowStartIso?: string;
  prorationWindowEndIso?: string;
  /** When switching to annual mid-cycle: first yearly renewal from org billing anchor (see backend). */
  nextRenewalDisplayIso?: string;
};

export type SubscriptionCheckoutCartTotals = {
  totalEur: number;
  /** VAT rate as a percentage (e.g. 19 for 19%). 0 when no VAT applies or address is unknown. */
  vatRate: number;
  planChangeAmountEur?: number;
  /** One row per cart plan line (same order as `cart.lines`). */
  planLines?: SubscriptionCheckoutCartPlanLineTotal[];
  addonLines: { quotaKey: string; quantity: number; amountEur: number }[];
};

export type SubscriptionCheckoutCartLineDto = {
  subscriptionProductKey: string;
  /** Present on persisted cart; may be absent briefly before PATCH resolves a sole catalog tier. */
  tier?: string;
  billingPeriod?: string;
  additionalQuotaUnits?: { quotaKey: string; quantity: number }[];
  parentSubscriptionInstanceId?: string;
};

export type SubscriptionCheckoutCartResponse = {
  cart: {
    lines?: SubscriptionCheckoutCartLineDto[];
    quotaAddons?: { quotaKey: string; quantity: number }[];
    updatedAt?: string;
  } | null;
  totals: SubscriptionCheckoutCartTotals;
  /** True when the org has at least one paid subscription order with an invoice — mirrors the event order guard. */
  hasInvoice: boolean;
};

/** Use for UI: derive manage-plan preview from `lines[0]`. */
export function getEffectivePlanChangeRaw(cart: SubscriptionCheckoutCartResponse['cart']): unknown {
  if (!cart) {
    return undefined;
  }
  return cart.lines?.[0];
}

export function useSubscriptionCheckoutCartQuery(organizationId: string | undefined) {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: [SUBSCRIPTION_CHECKOUT_CART_QUERY_KEY, organizationId],
    enabled: !!organizationId,
    queryFn: async (): Promise<SubscriptionCheckoutCartResponse> => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/transactions/organization/${organizationId}/subscription-checkout-cart`;
      return sendJSONRequest({ url, method: METHOD.GET, token: token ?? '' });
    },
  });
}
